import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, FilePlus2, FileText, Pencil, Trash2, Undo2, X } from 'lucide-react'
import { billChange, deleteChange, saveChange, setChangeStatus } from '../../api/jobs'
import Button from '../../components/Button'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import { StatusBadge } from '../../components/PageParts'
import { Card } from '../../components/Summary'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay, localToday } from '../../utils/format'
import { Field } from '../master/masterShared'
import { money } from './salesShared'

const TONE = { Waiting: 'warn', Approved: 'good', Declined: 'muted' }
const BLANK = { description: '', amount: '', cost_change: '', requested_date: '' }

const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${money(Math.abs(n))}`

// A job's change orders: agreed changes to the work and price after the
// quote. Approved ones add to the job's price (and, for a long job, its
// expected cost); an approved change that adds money is billed on its own
// draft invoice.
export default function JobChanges({ job, open, onChanged, onError }) {
  const nav = useNavigate()
  const { notify } = useToast()
  const [form, setForm] = useState(null)
  const [errors, setErrors] = useState({})
  const [approving, setApproving] = useState(null)
  const [busy, setBusy] = useState('')
  const changes = job.changes || []

  const run = async (key, fn, done) => {
    setBusy(key)
    onError('')
    try {
      const res = await fn()
      if (done) notify(done)
      await onChanged()
      return res
    } catch (e) {
      onError(e.message || 'That didn’t work. Please try again.')
      return null
    } finally {
      setBusy('')
    }
  }

  const save = async () => {
    setErrors({})
    setBusy('save')
    try {
      await saveChange(job.job_id, form)
      notify(form.change_id ? 'Change saved' : 'Change added')
      setForm(null)
      await onChanged()
    } catch (e) {
      setErrors(e.fields || { description: e.message || 'Couldn’t save the change.' })
    } finally {
      setBusy('')
    }
  }

  const approve = async () => {
    const ok = await run('approve', () => setChangeStatus(job.job_id, approving.change_id, {
      status: 'Approved', approved_date: approving.approved_date, approved_by: approving.approved_by,
    }), 'Change approved')
    if (ok) setApproving(null)
  }

  const bill = async (c) => {
    const res = await run(`bill${c.change_id}`, () => billChange(job.job_id, c.change_id))
    if (res?.invoice_id) nav(`/sales/customer-invoices/${res.invoice_id}`)
  }

  const action = open && <button type="button" className="sum-card-link" onClick={() => { setErrors({}); setForm({ ...BLANK, requested_date: localToday() }) }}>Add a change</button>

  return (
    <div id="changes">
      <Card title={<>Changes <HelpTip text="Agreed changes to the work after the quote, such as an extra socket (+) or tiles the customer supplies (−). Approved changes add to the job’s price; a waiting one counts once the customer agrees." /></>} action={action}>
        {changes.length ? (
          <ul className="sum-list">
            {changes.map((c) => (
              <li key={c.change_id} className="job-change">
                <span className="sum-list-main">
                  <strong>{c.number}</strong> {c.description}
                  <span className="cell-sub">
                    {[c.status === 'Approved' ? `Agreed ${fmtDay(c.approved_date)}${c.approved_by ? ` by ${c.approved_by}` : ''}` : `Asked ${fmtDay(c.requested_date)}`,
                      Math.abs(c.cost_change) > 0.004 ? `cost ${signed(c.cost_change)}` : ''].filter(Boolean).join(' · ')}
                    {c.billed && <> · <Link to={`/sales/customer-invoices/${c.invoice_id}`}>{c.invoice_number || 'Draft invoice'}</Link></>}
                  </span>
                  {open && (
                    <span className="job-change-actions">
                      {c.status === 'Waiting' && <>
                        <Button size="sm" variant="outline" onClick={() => setApproving({ ...c, approved_date: localToday(), approved_by: '' })}><Check size={13} />Approved</Button>
                        <Button size="sm" variant="ghost" loading={busy === `decline${c.change_id}`} onClick={() => run(`decline${c.change_id}`, () => setChangeStatus(job.job_id, c.change_id, { status: 'Declined' }), 'Marked declined')}><X size={13} />Declined</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setErrors({}); setForm({ change_id: c.change_id, description: c.description, amount: String(c.amount), cost_change: c.cost_change ? String(c.cost_change) : '', requested_date: c.requested_date || '' }) }}><Pencil size={13} />Edit</Button>
                      </>}
                      {c.status === 'Approved' && !c.billed && c.amount > 0 && (
                        <Button size="sm" variant="outline" loading={busy === `bill${c.change_id}`} onClick={() => bill(c)}><FileText size={13} />Bill it</Button>
                      )}
                      {c.status === 'Approved' && !c.billed && c.amount < 0 && (
                        <span className="cell-sub">Give the customer a credit memo for it.</span>
                      )}
                      {c.status !== 'Waiting' && !c.billed && (
                        <Button size="sm" variant="ghost" loading={busy === `undo${c.change_id}`} onClick={() => run(`undo${c.change_id}`, () => setChangeStatus(job.job_id, c.change_id, { status: 'Waiting' }), 'Back to waiting')}><Undo2 size={13} />Back to waiting</Button>
                      )}
                      {c.status !== 'Approved' && (
                        <Button size="sm" variant="ghost" loading={busy === `del${c.change_id}`} onClick={() => run(`del${c.change_id}`, () => deleteChange(job.job_id, c.change_id), 'Change deleted')}><Trash2 size={13} />Delete</Button>
                      )}
                    </span>
                  )}
                </span>
                <span className="sum-list-side">
                  {signed(c.amount)}
                  <span className="cell-sub"><StatusBadge label={c.status} tone={TONE[c.status]} /></span>
                </span>
              </li>
            ))}
          </ul>
        ) : <p className="sum-muted">No changes yet. When the customer asks for more (or less) work, add it here and mark it approved once they agree.</p>}
      </Card>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.change_id ? 'Edit the change' : 'Add a change'}
        subtitle="Extra work, or work taken out, after the quote. It counts once the customer agrees."
        footer={<>
          <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
          <Button onClick={save} loading={busy === 'save'}><FilePlus2 size={14} />{form?.change_id ? 'Save' : 'Add the change'}</Button>
        </>}
      >
        {form && <>
          <Field label="What changed" error={errors.description}>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Add a second socket in the kitchen" />
          </Field>
          <div className="ci-form-meta-top two">
            <Field label={<>Change in price <HelpTip text="Before sales tax. Negative when work is taken out, e.g. −250." /></>} error={errors.amount}>
              <input inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
            </Field>
            <Field label={<>Change in cost (optional) <HelpTip text="What the change adds to your costs: materials and labour. For a long job it updates the expected total cost, which sets how far along the job is." /></>} error={errors.cost_change}>
              <input inputMode="decimal" value={form.cost_change} onChange={(e) => setForm({ ...form, cost_change: e.target.value })} placeholder="0.00" />
            </Field>
          </div>
          <Field label="Asked for on" error={errors.requested_date}>
            <input type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} />
          </Field>
        </>}
      </Modal>

      <Modal
        open={!!approving}
        onClose={() => setApproving(null)}
        title={approving ? `Customer agreed to ${approving.number}` : ''}
        subtitle={approving ? `${approving.description} (${signed(approving.amount)}). From now on it’s part of the job’s price.` : ''}
        footer={<>
          <Button variant="ghost" onClick={() => setApproving(null)}>Cancel</Button>
          <Button onClick={approve} loading={busy === 'approve'}><Check size={14} />Mark approved</Button>
        </>}
      >
        {approving && <div className="ci-form-meta-top two">
          <Field label="Agreed on">
            <input type="date" value={approving.approved_date} onChange={(e) => setApproving({ ...approving, approved_date: e.target.value })} />
          </Field>
          <Field label={<>Agreed by (optional) <HelpTip text="Who agreed for the customer, e.g. “Mrs Smith, by text”. Keep their signed copy or message with the job." /></>}>
            <input value={approving.approved_by} onChange={(e) => setApproving({ ...approving, approved_by: e.target.value })} placeholder="Name, and how" />
          </Field>
        </div>}
      </Modal>
    </div>
  )
}
